import React, { useState } from "react";
import { Container } from "@mui/material";
import toast from "react-hot-toast";
import Header from "../../components/Header/Header";
import api from "../../config/apiConfig";
import AppLoader from "../../components/AppLoader/AppLoader";
import { set } from "date-fns";

const PMS: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const handleFileSelect = (selectedFiles: FileList) => {
    const validFiles = Array.from(selectedFiles).filter((file) =>
      file.name.endsWith(".xls") || file.name.endsWith(".xlsx")
    );

    if (validFiles.length === 0) {
      toast.error("Please upload only Excel files (.xls or .xlsx).");
      return;
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleSubmit = async () => {
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("file", file); // Append each file
      });
      formData.append("listingType", "pms");
      setLoading(true);

      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await api.post("admin/uploadListingData", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `${accessToken}`,
          },
        });
        setLoading(false);
        toast.success("Files uploaded successfully.");
        console.log(response.data);
        setFiles([]); // Clear files after upload
        setRefreshKey((prevKey) => prevKey + 1);
      } catch (error) {
        console.error("Error uploading files:", error);
        toast.error("Error uploading files.");
      }
    } else {
      toast.error("Please select at least one file.");
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <>
      <AppLoader loading={loading} />
      <Container
        key={refreshKey}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
          textAlign: "center",
        }}
      >
        <div
          className="flex flex-col items-center justify-center w-[60%] h-auto"
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div
            className={`w-full h-full flex flex-col items-center justify-center cursor-pointer ${
              dragActive ? "bg-gray-100" : "bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="dropzone-file"
                className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-5 hover:bg-gray-10"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                </div>
                <input
                  id="dropzone-file"
                  type="file"
                  className="hidden"
                  accept=".xls,.xlsx"
                  multiple
                  onChange={handleChange}
                />
              </label>
            </div>
          </div>

          {files.length > 0 && (
            <div className="mt-4 w-full">
              <strong>Selected Files:</strong>
              <ul className="mt-2 text-gray-700">
                {files.map((file, index) => (
                  <li key={index} className="flex justify-between items-center border-b py-2">
                    {file.name}
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex space-x-4">
            <label
              htmlFor="file-input"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
            >
              Browse Files
            </label>
            <input
              id="file-input"
              type="file"
              className="hidden"
              accept=".xls,.xlsx"
              multiple
              onChange={handleChange}
            />

            {files.length > 0 && (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Upload
              </button>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default PMS;
